/**
 * Status vocabulary for `nest-logistic-service` — **v2**.
 *
 * Single source of truth for the item/shipment status words this app reads.
 * Contract: [docs/status/07-API-CHANGES.md](../../../docs/status/07-API-CHANGES.md),
 * model: [docs/status/06-STATUS-MODEL-V2.md](../../../docs/status/06-STATUS-MODEL-V2.md).
 *
 * What v2 changed — **only two item values were actually renamed**:
 *   - item `DELIVERED` → `COMPLETED`, `RETURNED` → `RETURNED_TO_HUB`
 *   - item `DAMAGED` → gone as a status; it is now the `condition` attribute,
 *     so a damaged parcel keeps its real custody status and still ships
 *   - new item statuses `IN_DELIVERY`, `RETURNED_TO_CLIENT`; `LOST` is now a
 *     real (reversible) enum member instead of a ghost value
 *   - shipment gains `AT_HUB`, `RETURNED`, `FAILED` — nothing renamed
 *
 * `HUB_SCANNED`, `DISPATCHED` and `HANDOVER_SCANNED` all **stay** (07 §1): the
 * rider app consumes them, so the first v2 draft's plan to drop `DISPATCHED`
 * was withdrawn. Same for shipment `PARTIALLY_DISPATCHED` / `DISPATCHED` — both
 * are live rollup outcomes of a dispatch commit, not history.
 *
 * **Legacy item values are deliberately kept** in the union + label map so rows
 * written before the data migration still render instead of falling through as
 * a raw enum token. They are display-only: never send one as a `status=`
 * filter — the BE 400s on anything outside the v2 set.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Item status (`items.status`) — pure physical custody
// ─────────────────────────────────────────────────────────────────────────────

export const ITEM_STATUSES_V2 = [
  'CREATED',
  'HUB_SCANNED',
  // Dispatch committed, delivery born in DS — the parcel is *still at the hub*.
  // A planning fact kept as a status because the rider app reads it.
  'DISPATCHED',
  'HANDOVER_SCANNED',
  'IN_DELIVERY',
  'COMPLETED',
  'IN_RETURN',
  'RETURNED_TO_HUB',
  'RETURNED_TO_CLIENT',
  'CANCELLED',
  'LOST',
  'FAILED',
] as const;
export type ItemStatusV2 = (typeof ITEM_STATUSES_V2)[number];

/** Pre-v2 values. Rendered, never sent as a filter. */
export const LEGACY_ITEM_STATUSES = [
  'DELIVERED',
  'RETURNED',
  'DAMAGED',
] as const;
export type LegacyItemStatus = (typeof LEGACY_ITEM_STATUSES)[number];

/**
 * Anything the API can hand us for an item today: v2 plus the pre-migration
 * values. Use this for *reading* (row types, badges, labels) and
 * `ItemStatusV2` for *writing* (`status=` filters, tab keys).
 */
export type ItemStatus = ItemStatusV2 | LegacyItemStatus;

export const ITEM_STATUS_LABELS: Record<ItemStatus, string> = {
  CREATED: 'Created',
  HUB_SCANNED: 'Hub scanned',
  HANDOVER_SCANNED: 'Handover scanned',
  IN_DELIVERY: 'In delivery',
  COMPLETED: 'Completed',
  IN_RETURN: 'In return',
  RETURNED_TO_HUB: 'Returned to hub',
  RETURNED_TO_CLIENT: 'Returned to client',
  CANCELLED: 'Cancelled',
  LOST: 'Lost',
  FAILED: 'Failed',
  // Legacy
  DISPATCHED: 'Dispatched',
  DELIVERED: 'Delivered',
  RETURNED: 'Returned',
  DAMAGED: 'Damaged',
};

/** Pre-v2 → v2, per 06 §8. Use for grouping/filtering, not for display. */
export const LEGACY_ITEM_STATUS_MAP: Record<LegacyItemStatus, ItemStatusV2> = {
  DELIVERED: 'COMPLETED',
  // Conservative: pre-v2 `RETURNED` only ever meant "physically back at hub".
  RETURNED: 'RETURNED_TO_HUB',
  // `DAMAGED` carried no custody info; the closest honest v2 status is
  // "at hub", with the damage itself living in `condition`.
  DAMAGED: 'HUB_SCANNED',
};

export const isLegacyItemStatus = (
  status: string
): status is LegacyItemStatus =>
  (LEGACY_ITEM_STATUSES as readonly string[]).includes(status);

export const toItemStatusV2 = (status: ItemStatus): ItemStatusV2 =>
  isLegacyItemStatus(status) ? LEGACY_ITEM_STATUS_MAP[status] : status;

/** No transitions out. Note `LOST` is **not** here — it is reversible. */
export const TERMINAL_ITEM_STATUSES: readonly ItemStatus[] = [
  'COMPLETED',
  'RETURNED_TO_CLIENT',
  'CANCELLED',
  // Legacy equivalents
  'DELIVERED',
];

/** Not counted as active by the shipment rollup. */
export const INACTIVE_ITEM_STATUSES: readonly ItemStatus[] = [
  'CANCELLED',
  'LOST',
];

/** The retur branch — item is coming back, or already back. */
export const RETURN_ITEM_STATUSES: readonly ItemStatus[] = [
  'IN_RETURN',
  'RETURNED_TO_HUB',
  'RETURNED_TO_CLIENT',
  // Legacy equivalent
  'RETURNED',
];

// ─────────────────────────────────────────────────────────────────────────────
// Condition — the attribute that replaced item status `DAMAGED`
// ─────────────────────────────────────────────────────────────────────────────

export const ITEM_CONDITIONS = ['OK', 'DAMAGED'] as const;
export type ItemCondition = (typeof ITEM_CONDITIONS)[number];

export const ITEM_CONDITION_LABELS: Record<ItemCondition, string> = {
  OK: 'OK',
  DAMAGED: 'Damaged',
};

/**
 * True when the parcel is flagged damaged — either by the v2 `condition`
 * attribute or by a pre-migration row still sitting on status `DAMAGED`.
 */
export const isDamaged = (item: {
  status?: string | null;
  condition?: ItemCondition | null;
}): boolean => item.condition === 'DAMAGED' || item.status === 'DAMAGED';

// ─────────────────────────────────────────────────────────────────────────────
// Delivery legs — `item_delivery_legs`, one row per trip an item takes
// ─────────────────────────────────────────────────────────────────────────────

/** `FIRST_MILE` / `MIDDLE_MILE` are reserved names, not yet produced. */
export const ITEM_LEG_TYPES = [
  'FIRST_MILE',
  'MIDDLE_MILE',
  'LAST_MILE',
  'RETURN_TO_HUB',
  'RETURN_TO_CLIENT',
] as const;
export type ItemLegType = (typeof ITEM_LEG_TYPES)[number];

export const ITEM_LEG_TYPE_LABELS: Record<ItemLegType, string> = {
  FIRST_MILE: 'First mile',
  MIDDLE_MILE: 'Middle mile',
  LAST_MILE: 'Last mile',
  RETURN_TO_HUB: 'Return to hub',
  RETURN_TO_CLIENT: 'Return to client',
};

/**
 * One item↔delivery link. Replaces the old `externalDeliveryUid` /
 * `de2DeliveryUid` column pair: a failed attempt keeps its leg and the retrying
 * dispatch appends a new one, so delivery attempts are countable
 * (`legs.filter(l => l.legType === 'LAST_MILE').length`).
 */
export interface ItemDeliveryLeg {
  id: string;
  /** 1-based sequence within the item. */
  legSeq: number;
  legType: ItemLegType;
  /** Delivery UID in ts-delivery-service. */
  deliveryUid: string;
  /** Mirror of the last delivery status seen for this leg. */
  deliveryStatus: string | null;
  /** The leg currently in flight. At most one per item. */
  isActive: boolean;
  createdAt?: string;
}

/** The in-flight leg, if any. Falls back to scanning `legs` when absent. */
export const findActiveLeg = (
  legs?: ItemDeliveryLeg[] | null
): ItemDeliveryLeg | null => legs?.find((leg) => leg.isActive) ?? null;

/** Delivery attempts made so far = number of last-mile legs. */
export const countDeliveryAttempts = (
  legs?: ItemDeliveryLeg[] | null
): number => (legs ?? []).filter((leg) => leg.legType === 'LAST_MILE').length;

// ─────────────────────────────────────────────────────────────────────────────
// Shipment status (`shipments.status`) — derived, rollup of its items
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The ten shipment statuses, in journey order. Unlike items there is **no
 * legacy split here**: v2 renamed nothing on the shipment side (07 §3.5 — "nol
 * remap"), it only added `AT_HUB`, `RETURNED` and `FAILED`. So every value is
 * both renderable and a valid `status=` filter.
 */
export const SHIPMENT_STATUSES = [
  'CREATED',
  // Goods arrived — set on the *first* item scan. Completeness is a count
  // (`scanned/total`), never a status.
  'AT_HUB',
  // Both are rollup outcomes of a dispatch commit: some / all active items
  // scheduled. Live values, not history.
  'PARTIALLY_DISPATCHED',
  'DISPATCHED',
  'IN_DELIVERY',
  'IN_DISPUTE',
  'COMPLETED',
  'RETURNED',
  'CANCELLED',
  'FAILED',
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export const SHIPMENT_STATUS_LABELS: Record<ShipmentStatus, string> = {
  CREATED: 'Created',
  AT_HUB: 'At hub',
  PARTIALLY_DISPATCHED: 'Partially dispatched',
  DISPATCHED: 'Dispatched',
  IN_DELIVERY: 'In delivery',
  IN_DISPUTE: 'In dispute',
  COMPLETED: 'Completed',
  RETURNED: 'Returned',
  CANCELLED: 'Cancelled',
  FAILED: 'Failed',
};

/**
 * No transitions out — the shipment is settled. Mirrors the BE force-terminal
 * guard: a terminal shipment 400s (`INVALID_STATE`) on both cancel and override.
 */
export const SHIPMENT_TERMINAL_STATUSES: readonly ShipmentStatus[] = [
  'COMPLETED',
  'RETURNED',
  'CANCELLED',
  'FAILED',
];

/**
 * In-flight statuses the OPS force-terminal override accepts (BE contract). A
 * `CREATED` shipment (use cancel instead) and any terminal status are excluded.
 */
export const SHIPMENT_FORCE_TERMINAL_STATUSES: readonly ShipmentStatus[] = [
  'AT_HUB',
  'PARTIALLY_DISPATCHED',
  'DISPATCHED',
  'IN_DELIVERY',
  'IN_DISPUTE',
];

/** Cancel is only valid while the shipment is still `CREATED` (BE 400s otherwise). */
export const canCancelShipment = (status: ShipmentStatus): boolean =>
  status === 'CREATED';

/**
 * The Mark Completed / Mark Failed override is only valid for an in-flight
 * shipment — hidden for `CREATED` and for any terminal status.
 */
export const canForceTerminalShipment = (status: ShipmentStatus): boolean =>
  SHIPMENT_FORCE_TERMINAL_STATUSES.includes(status);

/**
 * Two things about shipment status that the UI must not design against:
 *
 * 1. **It can move backwards.** It is derived from item counts, so three
 *    backward moves are legitimate (09 §5): `IN_DELIVERY → AT_HUB` (failed
 *    attempt re-scanned at the hub), `FAILED → AT_HUB` (a lost parcel turns
 *    up), `COMPLETED → AT_HUB` (new items appended to a closed AWB). Never
 *    assume monotonic progress.
 * 2. **`PARTIALLY_DISPATCHED` / `DISPATCHED` are internal ops words** (09 §8).
 *    The plan is to collapse `AT_HUB`/`PARTIALLY_DISPATCHED`/`DISPATCHED` →
 *    `AT_HUB` and `IN_DISPUTE` → `IN_RETURN` at the external-API boundary —
 *    not built yet, and these screens are ops-facing, so they show the raw
 *    value on purpose. A client-facing view added here would need that
 *    collapse applied locally first.
 */
